import React, { useSyncExternalStore } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { Brightness4, Brightness7 } from '@mui/icons-material';
import { useColorMode } from '../../hooks/useColorMode';

export interface ThemeToggleProps {
  size?: 'small' | 'medium' | 'large';
  color?: 'inherit' | 'default' | 'primary' | 'secondary';
}

const emptySubscribe = () => () => {};
const useHasMounted = () =>
  useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false,
  );

/**
 * Simple icon button to toggle between light and dark mode
 *
 * @example
 * ```tsx
 * <ThemeToggle size="small" color="inherit" />
 * ```
 */
export const ThemeToggle = (props: ThemeToggleProps) => {
  const { size = 'medium', color = 'inherit' } = props;
  const { mode, toggleMode } = useColorMode();
  const mounted = useHasMounted();
  const isDark = mode === 'dark';

  return (
    <Tooltip title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
      <div suppressHydrationWarning>
        <IconButton
          onClick={toggleMode}
          color={color}
          size={size}
          aria-label="toggle theme mode"
        >
          {mounted ? (
            isDark ? (
              <Brightness4 fontSize={size} />
            ) : (
              <Brightness7 fontSize={size} />
            )
          ) : (
            // Render a placeholder during SSR to maintain layout
            <Brightness7 fontSize={size} style={{ opacity: 0 }} />
          )}
        </IconButton>
      </div>
    </Tooltip>
  );
};

export default ThemeToggle;
